import {
  createContext,
  useCallback,
  useContext,
  type ReactNode,
} from "react";
import { useAuth } from "../hooks/useAuth";
import { useOrgEntitlements } from "../hooks/useOrgEntitlements";
import { OrgEntitlements } from "../models/SubscriptionModels";

export interface UseEntitlementsReturn {
  entitlements: OrgEntitlements | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const EntitlementsContext = createContext<UseEntitlementsReturn | undefined>(
  undefined,
);

// Loads the current organization's entitlements on app shell / org switch and
// keeps them in a single place. Consumers read via useEntitlements().
export const EntitlementsProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const organizationId = user?.activeOrganization?.organizationId;

  const { entitlements, loading, error, reload } =
    useOrgEntitlements(organizationId);

  const refresh = useCallback(async () => {
    await reload();
  }, [reload]);

  return (
    <EntitlementsContext.Provider
      value={{ entitlements, loading, error, refresh }}
    >
      {children}
    </EntitlementsContext.Provider>
  );
};

export const useEntitlements = (): UseEntitlementsReturn => {
  const ctx = useContext(EntitlementsContext);
  if (!ctx)
    throw new Error("useEntitlements must be used within an EntitlementsProvider");
  return ctx;
};